<script lang="ts">
    import { flip } from 'svelte/animate'
    import { slide } from 'svelte/transition'
    import {dragObject} from 'src/components/cabinet/app-configs/components/dragObject'
    import type {ITreeListNode} from 'src/components/#common/tree-list/TreeListNode'
    import {TreeListNode} from 'src/components/#common/tree-list/TreeListNode'
    import type {CategoryState} from 'src/components/cabinet/app-configs/components/contracts'
    import {doShow as showAlert} from 'src/components/#common/alert/store'
    import Category from 'src/components/cabinet/app-configs/components/Category.svelte'
    import {isDragging} from 'src/components/cabinet/app-configs/components/isDragging'

    export let id
    export let status = false
    export let firstDraggable
    export let categories: ITreeListNode<CategoryState>
    export let level: number = 0
    export let maxLevels: number = 5

    export let searchText

    categories.on('change', () => { categories = categories })

    let hoveringIndex: number

    function allowToDrop(category: ITreeListNode<CategoryState>) {
        return category
        // ограничение максимальной глубины
        && category.maxDepth + level < maxLevels
        // защита от бесконечной вложенности
        && !category.isParentOf(categories)
    }

    const drop = (event, index) => {
        if ($dragObject == null) {
            console.warn('dragObject is null')
            return
        }

        if (!allowToDrop($dragObject?.object)) {
            showAlert.set({
                status: 'message',
                text: 'WARNING! The nesting level of the selected category is too large.'
            })
            return
        }

        event.dataTransfer.dropEffect = 'move'

        // вставляем категорию по индексу с автоматическим удалением из предыдущего родителя
        const { object } = $dragObject
        categories.insert(index, object)
        hoveringIndex = null

        if (!firstDraggable) {
            firstDraggable = true
            showAlert.set({
                status: 'message',
                text: 'WARNING! You have changed the category nesting. The config may not work correctly.'
            })
        }
    }

    function addCategory(category) {
        category.insert(0, new TreeListNode({
            data: {
                name: 'Category ' + (category.length + 1),
                description: '',
                type: 'object',
                value: {
                    id: category.length,
                }
            },
        }))
    }

    $: if ($dragObject == null) {
        hoveringIndex = null
    }
</script>

<div
    class="list"
    class:main-level={level === 0}
    class:dragging={$isDragging}
>
    {#if level === 0}
        <div class="first-category__add-button" on:click={() => { addCategory(categories) }}>+ Add category</div>
    {/if}
    {#if (level === 0 || status) && categories.length}
        <div class="list__items" transition:slide={{duration: 350}}>
            {#each categories.toArray() as category, index (category)}
                <div class="list-item-wrapper" animate:flip={{duration: 350}}>
                    <Category
                        on:select
                        {category}
                        {index}
                        {status}
                        bind:firstDraggable
                        {searchText}
                        {maxLevels}
                        {level}
                    />
                </div>
            {/each}
        </div>
    {/if}
    {#if level < maxLevels}
        <div class="list-item__name--add"
            on:dragenter={() => {
                hoveringIndex = categories.length
            }}
            on:dragleave={(event) => {
                if (hoveringIndex === categories.length) {
                    hoveringIndex = null
                }
            }}
            ondragover="return false"
            on:drop|preventDefault={event => { drop(event, categories.length) }}
            class:is-active={hoveringIndex === categories.length}>
        </div>
    {/if}
</div>

<style>
    .main-level {
        padding: 0 8px 8px 0;
    }

    .list {
        position: relative;
    }

    .list-item__name--add {
        position: relative;
        width: 100%;
        height: 7px;
        margin-top: 0;
        margin-bottom: -2px;
        z-index: 1;
    }

    .list-item__name--add:hover {
        border-color: #ff5d1c;
    }

    .list__items {
        position: relative;
    }

    .list.main-level > .list__items {
        max-height: initial !important;
        overflow: initial;
    }

    .list-item__name--add.is-active:after {
        content: "";
        height: 5px;
        width: calc(100% - 16px);
        background: #0080ff;
        position: absolute;
        top: 2px;
        right: 0;
        left: 15px;
        border-radius: 5px;
    }

    .dragging .list-item__name > * {
        pointer-events: none;
    }

    .category-actions button {
        border: 0;
        background: #eff1f5;
        color: #4a5463;
        margin-left: 5px;
        border-radius: 4px;
        padding: 0;
        width: 25px;
        transition: .2s;
    }

    .category-actions button:hover {
        background: #d0d5df;
    }

    .first-category__add-button {
        font-weight: 400;
        border-radius: 4px;
        border: 1px dashed #d3d3d3;
        margin: 8px 0 3px 12px;
        font-size: 13px;
        text-align: center;
        padding: 4px;
        transition: .2s;
        position: relative;
        cursor: pointer;
        user-select: none;
    }

    .first-category__add-button:hover {
        border-color: #ff5d1c;
    }

    .list-item-wrapper {
        padding-left: 1px;
        padding-bottom: 1px;
    }

    :global(.list.main-level>.list__items>.list-item-wrapper) {
        padding-left: 0;
    }
</style>
